import { useAuth } from "react-oidc-context";
import { InvoiceForm } from "./_components/createInvoice";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";
import {
    getInvoiceSenderQueryOptions,
    getInvoiceQueryOptions,
    getOcrResponseQueryOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import { InvoicePageSkeleton } from "./_components/InvoicePageSkeleton";
import {
    getClaimsInfoByIdQueryOptions,
    getClaimRecipientDetailsQueryOptions,
    getCompanyAssignedSubclaimsOfClaimQueryOptions
} from "@/api/queryOptions/claimsApiQueryOptions";

import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import { useTranslation } from "react-i18next";
import { Box, Breadcrumb, Text } from "@chakra-ui/react";
import { getUserRoleQueryOption } from "@/api/queryOptions/usersAPIQueryOptions";
import InvoiceReceipt from "./_components/invoiceReceipt";
import { InvoiceDetailsDto, invoiceStatusValues } from "@/types";

const Invoice = () => {
    const { t } = useTranslation(["invoices", "main"]);
    const userInfo = useAuth();
    const customAttributes = userInfo.user?.profile?.custom_attributes as CustomAttributes | undefined;
    const loggedInUserCrmId = customAttributes?.Company_ID__c;

    const { data: userRoleResponse } = useQuery(getUserRoleQueryOption(Number(loggedInUserCrmId)));
    const userRole = userRoleResponse ?? "ESP";

    // current crmId while logged in, either from act on behalf of or user's own company
    const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
    const currentCrmId = actOnBehalfOf ? JSON.parse(actOnBehalfOf).crmId.toString() : loggedInUserCrmId;

    const invoiceId = useParams<{ invoiceId: string }>().invoiceId || "";

    const {
        invoice,
        invoiceSender,
        ocrResponse,
        claim,
        assignedSubclaimRefs,
        invoiceIsFetching,
        isLoadingInvoice,
        isLoadingClaim,
        ocrResponseIsLoading
    } = useInvoiceData(invoiceId, currentCrmId);

    useSlowRequestToast({
        isLoading: invoiceIsFetching
    });

    // If the invoice has a submitted on behalf of another company, use that company's crmId for related data,
    // otherwise use the current company crmId
    const crmId = invoice?.submittedOnBehalfOfCrmId || currentCrmId;

    if (isLoadingInvoice || !invoice) {
        return <InvoicePageSkeleton />;
    }

    return (
        <Box>
            <Breadcrumb.Root ml={{ base: "10%", lg: "11%" }} mt="1rem">
                <Breadcrumb.List>
                    <Breadcrumb.Item>
                        <Breadcrumb.Link href="/" tabIndex={0} color="anchor.navy.600">
                            {t("main:home")}
                        </Breadcrumb.Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Separator />
                    <Breadcrumb.Item>
                        <Text>{t("invoices:createInvoice.title")}</Text>
                    </Breadcrumb.Item>
                </Breadcrumb.List>
            </Breadcrumb.Root>
            {shouldRenderInvoiceForm(invoice.status) ? (
                <InvoiceForm
                    invoice={invoice}
                    ocrResponse={ocrResponse}
                    invoiceSender={invoiceSender}
                    crmId={crmId}
                    claim={claim}
                    assignedSubclaimRefs={assignedSubclaimRefs}
                    isLoadingOcr={ocrResponseIsLoading && !isLoadingInvoice}
                    isLoadingClaim={isLoadingClaim}
                    userRole={userRole || "ESP"}
                />
            ) : (
                <InvoiceReceipt invoice={invoice} invoiceSender={invoiceSender} claim={claim} crmId={crmId} />
            )}
        </Box>
    );
};

export default Invoice;

type CustomAttributes = {
    Company_ID__c?: string;
};

const getClaimReference = (
    invoice: InvoiceDetailsDto | undefined,
    ocrResponse: InvoiceFromOcrDto | undefined
): string => {
    if (invoice?.claimsRef) {
        return invoice.claimsRef;
    }
    if (ocrResponse?.claimsRef) {
        return ocrResponse.claimsRef;
    }
    if (ocrResponse?.subClaimsRef) {
        return ocrResponse.subClaimsRef.split("-")[0];
    }
    return "";
};

const useInvoiceData = (invoiceId: string, currentCrmId: string | undefined) => {
    const queryClient = useQueryClient();

    const {
        data: invoice,
        isLoading: invoiceIsLoading,
        isFetching: invoiceIsFetching
    } = useQuery(getInvoiceQueryOptions(invoiceId));
    // If the invoice has a submitted on behalf of company id, use that crmId for fetching related data,
    // otherwise use the current company crmId
    const actOnBehalfOfCompanyId = invoice?.submittedOnBehalfOfCrmId;
    const invoiceSenderCompanyId = actOnBehalfOfCompanyId || currentCrmId || "";

    const { data: invoiceSender, isLoading: invoiceSenderIsLoading } = useQuery(
        getInvoiceSenderQueryOptions(invoiceSenderCompanyId, invoiceId)
    );
    const docid = invoice?.attachment?.documentId || "";
    const { data: ocrResponse, isLoading: ocrResponseIsLoading } = useQuery(
        getOcrResponseQueryOptions(invoiceId, docid, shouldRenderInvoiceForm(invoice?.status))
    );

    const claimRef = getClaimReference(invoice, ocrResponse);
    const { data: claim, isLoading: isLoadingClaim } = useQuery(getClaimsInfoByIdQueryOptions(claimRef));

    // The form defaults need these to pick a subclaim when the company is assigned to only one of them.
    const { data: assignedSubclaimRefs, isLoading: isLoadingAssignedSubclaims } = useQuery(
        getCompanyAssignedSubclaimsOfClaimQueryOptions(claimRef, invoiceSenderCompanyId)
    );

    // Prefetch claim recipient details if claim exists and companyCrmId are available
    useEffect(() => {
        if (claim?.id && invoiceSenderCompanyId) {
            queryClient.prefetchQuery(getClaimRecipientDetailsQueryOptions(claim.id, invoiceSenderCompanyId));
        }
    }, [claim?.id, invoiceSenderCompanyId, invoice?.status, queryClient]);

    const isLoadingInvoice = invoiceSenderIsLoading || invoiceIsLoading;

    return {
        invoice,
        invoiceSender,
        ocrResponse,
        claim,
        assignedSubclaimRefs,
        invoiceIsFetching,
        isLoadingInvoice,
        ocrResponseIsLoading,
        isLoadingClaim: isLoadingClaim || isLoadingAssignedSubclaims
    };
};

const shouldRenderInvoiceForm = (status: string | undefined): boolean =>
    status === invoiceStatusValues.Draft || status === invoiceStatusValues.Error;
